import { useCallback, useEffect, useMemo, useState } from "react";
import {
  Box, Stack, Typography, Alert, CircularProgress, IconButton, Tooltip, Chip,
  FormControlLabel, Switch, Paper, TextField, MenuItem, Tabs, Tab, Button,
  Table, TableHead, TableBody, TableRow, TableCell, TableContainer,
} from "@mui/material";
import RefreshIcon from "@mui/icons-material/Refresh";
import DownloadIcon from "@mui/icons-material/Download";
import KeyboardArrowDownIcon from "@mui/icons-material/KeyboardArrowDown";
import KeyboardArrowRightIcon from "@mui/icons-material/KeyboardArrowRight";
import { fmtArDateTime } from "../services/monitoring";
import {
  getSerieMensual, getSerieMensualDias, getLiquidacionesDiarias, fmtMoney, fmtBd, fmtMonth, type SerieSource,
  ACQ_CMF, CUENTA_CMF_COMPARTIDA, CUENTAS, matchCuenta, cuentaSufijo, type Cuenta,
  type SerieMensual, type SerieMesCell, type SerieDias, type SerieCollectorRow,
} from "../services/monitoreoVivo";

// Sin auto-refresh a propósito: el rollup lo escribe un job nocturno, no es un feed
// en vivo como /monitoreo/cobros. Solo el botón de refrescar.

/** Rangos ofrecidos. `months` es lo que se le PIDE al backend; los presets de un
 *  solo mes piden 3 y recortan la vista (ver `mesesVisibles`): así el mes a
 *  mostrar lo decide el rollup (`coverage.partial_month`) y no el reloj del
 *  navegador — un cobro del último viernes a la tarde cae en el mes siguiente y
 *  el calendario del cliente se equivocaría. Además ambos presets comparten
 *  request, así que alternarlos pega en el mismo cache de 300 s del backend. */
const RANGOS = [
  { value: "curso",   label: "Mes en curso",        months: 3  },
  { value: "cerrado", label: "Último mes completo", months: 3  },
  { value: "6",       label: "Últimos 6",           months: 6  },
  { value: "12",      label: "Últimos 12",          months: 12 },
  { value: "24",      label: "Últimos 24",          months: 24 },
] as const;

type Rango = (typeof RANGOS)[number]["value"];

const mesesPedidos = (r: Rango) => RANGOS.find((o) => o.value === r)!.months;

/** yyyyMM → "2026-08" (encabezado de CSV: ordena bien en Excel). */
const mesIso = (m: string) => (/^\d{6}$/.test(m) ? `${m.slice(0, 4)}-${m.slice(4)}` : m);

const csvEsc = (v: unknown) => {
  const t = v == null ? "" : String(v);
  return /[",\n;]/.test(t) ? `"${t.replace(/"/g, '""')}"` : t;
};

const EMPTY: string[] = [];

const nf = new Intl.NumberFormat("es-AR");

function StatTile({ label, value, sub, color }: { label: string; value: string; sub?: string; color?: string }) {
  return (
    <Box sx={{ minWidth: 180, p: 1.5, borderRadius: 2, border: "1px solid rgba(0,0,0,0.08)" }}>
      <Typography variant="caption" sx={{ color: "text.secondary", fontWeight: 700 }}>{label}</Typography>
      <Typography sx={{ fontSize: 22, fontWeight: 800, lineHeight: 1.15, color: color ?? "#222" }}>{value}</Typography>
      {sub && <Typography variant="caption" color="text.secondary">{sub}</Typography>}
    </Box>
  );
}

/** Celda de la matriz: cantidad arriba, monto abajo. `null` → "—" (fuera de cobertura).
 *  `live`: CP-424 — cuando ya se abrió el detalle diario de ESTA fila/mes, no hace falta
 *  pedir nada nuevo: el total del detalle (consulta en vivo) ya está disponible y es más
 *  fresco que el rollup, así que se usa para corregir la celda en el momento. Pedir esto
 *  para TODAS las filas de la tabla sería caro (re-escanea el mes por collector); hacerlo
 *  solo para la fila que el usuario ya abrió es gratis — no dispara ningún request nuevo. */
function MesCell({ cell, currency, onOpen, open, live }: {
  cell: SerieMesCell | null | undefined; currency: string;
  onOpen: () => void; open: boolean; live?: boolean;
}) {
  if (cell == null) {
    return (
      <TableCell align="right">
        <Tooltip title="Sin datos: el mes está fuera de la cobertura del rollup (no es cero cobros)">
          <Typography variant="body2" sx={{ color: "text.disabled" }}>—</Typography>
        </Tooltip>
      </TableCell>
    );
  }

  return (
    <TableCell
      align="right"
      onClick={onOpen}
      sx={{
        cursor: "pointer",
        bgcolor: open ? "#F0FDF4" : undefined,
        "&:hover": { bgcolor: "#F8FAFC" },
      }}
    >
      <Stack direction="row" spacing={0.5} justifyContent="flex-end" alignItems="center">
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          {nf.format(cell.count)}
        </Typography>
        {live && (
          <Tooltip title="Valor en vivo (del detalle diario), más fresco que el total mensual guardado">
            <Chip size="small" label="en vivo" color="success" variant="outlined" sx={{ height: 16, fontSize: 9 }} />
          </Tooltip>
        )}
      </Stack>
      <Typography variant="caption" color="text.secondary">
        {fmtMoney(cell.amount, currency)}
      </Typography>
    </TableCell>
  );
}

export default function MonitoreoSerieMensual() {
  const [data, setData] = useState<SerieMensual | null>(null);
  const [loading, setLoading] = useState(false);
  const [err, setErr] = useState<string | null>(null);
  const [rango, setRango] = useState<Rango>("6");
  const [source, setSource] = useState<SerieSource>("liquidado");
  const [filtro, setFiltro] = useState("");
  const [soloActivos, setSoloActivos] = useState(true);
  const [proc, setProc] = useState<string>("");
  const [cuenta, setCuenta] = useState<Cuenta>("todas");
  const [diarioBusy, setDiarioBusy] = useState(false);
  const [updatedAt, setUpdatedAt] = useState<string | null>(null);

  // Drill-down: {acquirer, collector, month} de la celda abierta.
  const [detalle, setDetalle] = useState<{ acq: string; collector: string; month: string } | null>(null);
  const [dias, setDias] = useState<SerieDias | null>(null);
  const [diasLoading, setDiasLoading] = useState(false);
  const [diasErr, setDiasErr] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true); setErr(null);
    try {
      const d = await getSerieMensual({ months: mesesPedidos(rango), source });
      setData(d);
      setUpdatedAt(new Date().toISOString());
    } catch (e: any) {
      setErr(e?.message ?? "Error al cargar");
    } finally {
      setLoading(false);
    }
  }, [rango, source]);

  useEffect(() => { load(); }, [load]);
  // Cambiar de ventana o de fuente invalida el detalle abierto.
  useEffect(() => { setDetalle(null); setDias(null); }, [rango, source]);

  useEffect(() => {
    if (!detalle) { setDias(null); setDiasErr(null); return; }
    let cancelado = false;
    setDiasLoading(true); setDiasErr(null);
    getSerieMensualDias({ acquirer: detalle.acq, collector: detalle.collector, month: detalle.month, source })
      .then((d) => { if (!cancelado) setDias(d); })
      .catch((e) => { if (!cancelado) setDiasErr(e?.message ?? "Error al cargar el detalle"); })
      .finally(() => { if (!cancelado) setDiasLoading(false); });
    return () => { cancelado = true; };
  }, [detalle, source]);

  const procs = useMemo(() => (data?.acquirers ?? []).map((a) => a.key), [data]);
  const activeProc = procs.includes(proc) ? proc : (procs[0] ?? "");
  const acq = useMemo(
    () => (data?.acquirers ?? []).find((a) => a.key === activeProc) ?? null,
    [data, activeProc],
  );

  // El selector de cuenta solo se ofrece en la solapa AR#CMFP; en el resto se
  // oculta y no filtra.
  const hayCuentaCompartida = activeProc === ACQ_CMF;
  const cuentaActiva: Cuenta = hayCuentaCompartida ? cuenta : "todas";

  const mesesCargados = data?.months ?? EMPTY;
  const parcial = data?.coverage.partial_month ?? "";
  // Último mes con cobertura: más allá las celdas son null ("no sabemos"), no cero.
  const ultimoCubierto = (data?.coverage.last_date ?? "").slice(0, 6);

  /** Meses que se MUESTRAN. Para los presets de un mes el corte lo decide el
   *  rollup (`partial_month` / `last_date`), no el calendario del navegador. Si
   *  no se puede resolver, se muestra lo cargado antes que una tabla vacía. */
  const meses = useMemo(() => {
    if (!mesesCargados.length) return mesesCargados;
    const cubiertos = ultimoCubierto
      ? mesesCargados.filter((m) => m <= ultimoCubierto)
      : mesesCargados;
    if (rango === "curso") {
      const m = parcial || cubiertos[cubiertos.length - 1];
      return m ? [m] : mesesCargados;
    }
    if (rango === "cerrado") {
      const m = [...cubiertos].reverse().find((x) => x !== parcial);
      return m ? [m] : mesesCargados;
    }
    return mesesCargados;
  }, [mesesCargados, parcial, ultimoCubierto, rango]);

  /** Suma de un collector sobre los meses visibles. Las celdas null no suman:
   *  fuera de cobertura no es cero. */
  const totalDeFila = useCallback((c: SerieCollectorRow) => {
    let count = 0, amount = 0;
    for (const m of meses) {
      const cell = c.months[m];
      if (cell) { count += cell.count; amount += cell.amount; }
    }
    return { count, amount };
  }, [meses]);

  // Actividad y orden se calculan sobre lo VISIBLE: con un mes solo, ordenar por
  // el total de la ventana pedida pondría primero a quien no cobró ese mes. Para
  // "Últimos 6/12/24" visible == pedido, así que reproduce el orden del backend.
  const rows = useMemo(() => {
    if (!acq) return [];
    const f = filtro.trim().toLowerCase();
    return acq.collectors
      .map((c) => ({ c, tot: totalDeFila(c) }))
      .filter(({ c, tot }) => {
        if (!matchCuenta(cuentaActiva, c.account)) return false;
        if (soloActivos && tot.count === 0) return false;
        if (f && !c.collector.toLowerCase().includes(f)) return false;
        return true;
      })
      .sort((a, b) => b.tot.amount - a.tot.amount)
      .map(({ c }) => c);
  }, [acq, filtro, soloActivos, cuentaActiva, totalDeFila]);

  /** Totales por mes del universo elegido. Con "Todas" son los del acquirer
   *  (backend); con una cuenta se suman los collectors de esa cuenta — sin
   *  aplicar buscador ni "solo con actividad", que son filtros de vista, no de
   *  universo. Fuera de cobertura (null) no suma. */
  const totalesPorMes = useMemo(() => {
    if (!acq) return {} as Record<string, { count: number; amount: number }>;
    if (cuentaActiva === "todas") return acq.totals_by_month;
    const out: Record<string, { count: number; amount: number }> = {};
    for (const c of acq.collectors) {
      if (!matchCuenta(cuentaActiva, c.account)) continue;
      for (const m of meses) {
        const cell = c.months[m];
        if (!cell) continue;
        const t = out[m] ?? (out[m] = { count: 0, amount: 0 });
        t.count += cell.count; t.amount += cell.amount;
      }
    }
    return out;
  }, [acq, cuentaActiva, meses]);

  const collectorsUniverso = useMemo(
    () => (acq?.collectors ?? []).filter((c) => matchCuenta(cuentaActiva, c.account)).length,
    [acq, cuentaActiva],
  );

  const totalMostrado = useMemo(() => {
    let count = 0, amount = 0;
    for (const m of meses) {
      const t = totalesPorMes[m];
      if (t) { count += t.count; amount += t.amount; }
    }
    return { count, amount };
  }, [totalesPorMes, meses]);

  // Variación del último mes CERRADO contra el anterior (el mes en curso está
  // incompleto: compararlo daría una caída falsa). Usa TODO lo cargado, no solo
  // lo visible: con un preset de un mes el mes previo igual vino en el request.
  const variacion = useMemo(() => {
    if (!acq || mesesCargados.length < 2) return null;
    const cerrados = mesesCargados.filter((m) => m !== data?.coverage.partial_month);
    if (cerrados.length < 2) return null;
    const [prev, last] = [cerrados[cerrados.length - 2], cerrados[cerrados.length - 1]];
    const montoMes = (m: string) => {
      if (cuentaActiva === "todas") return acq.totals_by_month[m]?.amount ?? 0;
      return acq.collectors.reduce((s, c) =>
        matchCuenta(cuentaActiva, c.account) ? s + (c.months[m]?.amount ?? 0) : s, 0);
    };
    const a = montoMes(prev);
    const b = montoMes(last);
    if (!a) return null;
    return { pct: ((b - a) / a) * 100, last, prev };
  }, [acq, mesesCargados, data, cuentaActiva]);

  /** Exporta exactamente lo que se ve: acquirer activo, meses visibles y filtros
   *  aplicados; una columna de cantidad y otra de monto por mes. */
  const etiquetaTotal = `TOTAL ${activeProc}${cuentaSufijo(cuentaActiva)}`;

  const descargarCsv = () => {
    if (!acq || !data) return;
    const linea = (vals: unknown[]) => vals.map(csvEsc).join(",");
    // CP-424: mismo "Datos hasta" que se ve en pantalla, para que un CSV compartido
    // no pierda el corte real (distinto de cuándo se generó el archivo).
    const lines = [linea([`Datos hasta: ${fmtBd(data.coverage.through_date || data.coverage.last_date)}`]),
      linea(["collector", "sub_processor", "account", "moneda",
      ...meses.flatMap((m) => [`${mesIso(m)} cantidad`, `${mesIso(m)} monto`])])];
    for (const r of rows) {
      lines.push(linea([r.collector, r.sub_processor, r.account, acq.currency,
        // Fuera de cobertura va VACÍO, no 0: en una planilla no son lo mismo.
        ...meses.flatMap((m): (string | number)[] => {
          const cell = r.months[m];
          return cell ? [cell.count, cell.amount] : ["", ""];
        })]));
    }
    // Igual que en pantalla: el total es del universo (acquirer o cuenta elegida),
    // no de lo que dejan pasar el buscador / "solo con actividad".
    lines.push(linea([etiquetaTotal, "", "", acq.currency,
      ...meses.flatMap((m) => {
        const t = totalesPorMes[m];
        return [t?.count ?? 0, t?.amount ?? 0];
      })]));
    const tramo = meses.length === 1 ? meses[0] : `${meses[0]}-${meses[meses.length - 1]}`;
    const sufCuenta = cuentaActiva === "todas" ? "" : `_${cuentaActiva === "comp" ? "cta-compartida" : "ctas-propias"}`;
    // BOM: sin esto Excel en Windows rompe los acentos.
    const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
    const url = URL.createObjectURL(blob);
    const a = document.createElement("a");
    a.href = url;
    a.download = `serie-mensual_${acq.key.replace(/[^A-Za-z0-9]+/g, "-")}${sufCuenta}_${tramo}.csv`;
    document.body.appendChild(a); a.click(); document.body.removeChild(a);
    URL.revokeObjectURL(url);
  };

  /** Reporte descargable pedido por operaciones: monto LIQUIDADO por DÍA sumando
   *  TODOS los collectors, respetando el procesador y la cuenta elegidos. Trae las
   *  filas diarias (por día/acquirer/cuenta) y las agrega por fecha aplicando el
   *  mismo filtro de cuenta que la pantalla (matchCuenta). */
  const descargarDiario = async () => {
    if (!acq || !data) return;
    setDiarioBusy(true);
    try {
      const d = await getLiquidacionesDiarias({ months: mesesPedidos(rango), acquirer: activeProc });
      // Agrega por fecha las filas cuya cuenta pasa el filtro activo.
      const porDia = new Map<string, { count: number; amount: number }>();
      for (const r of d.rows) {
        if (!matchCuenta(cuentaActiva, r.account)) continue;
        const acc = porDia.get(r.date) ?? { count: 0, amount: 0 };
        acc.count += r.count; acc.amount += r.amount;
        porDia.set(r.date, acc);
      }
      const fechas = [...porDia.keys()].sort();
      const linea = (vals: unknown[]) => vals.map(csvEsc).join(",");
      const lines = [linea([`Datos hasta: ${fmtBd(data.coverage.through_date || data.coverage.last_date)}`]),
        linea(["fecha", "procesador", "cuenta", "moneda", "cantidad_liquidaciones", "monto_liquidado"])];
      let totC = 0, totA = 0;
      const etiqCuenta = cuentaActiva === "todas" ? "todas"
        : cuentaActiva === "comp" ? "cuenta compartida" : "cuentas propias";
      for (const f of fechas) {
        const t = porDia.get(f)!;
        totC += t.count; totA += t.amount;
        lines.push(linea([`${f.slice(0, 4)}-${f.slice(4, 6)}-${f.slice(6, 8)}`,
          activeProc, etiqCuenta, acq.currency, t.count, t.amount]));
      }
      lines.push(linea([`TOTAL ${activeProc}${cuentaSufijo(cuentaActiva)}`, "", "", acq.currency, totC, totA]));
      const tramo = meses.length === 1 ? meses[0] : `${meses[0]}-${meses[meses.length - 1]}`;
      const sufCuenta = cuentaActiva === "todas" ? "" : `_${cuentaActiva === "comp" ? "cta-compartida" : "ctas-propias"}`;
      const blob = new Blob(["\ufeff" + lines.join("\n")], { type: "text/csv;charset=utf-8;" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `liquidado-diario_${activeProc.replace(/[^A-Za-z0-9]+/g, "-")}${sufCuenta}_${tramo}.csv`;
      document.body.appendChild(a); a.click(); document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch (e: any) {
      setErr(e?.message ?? "Error al generar el reporte diario");
    } finally {
      setDiarioBusy(false);
    }
  };

  const toggleCelda = (collector: string, month: string) => {
    setDetalle((cur) =>
      cur && cur.acq === activeProc && cur.collector === collector && cur.month === month
        ? null
        : { acq: activeProc, collector, month });
  };

  return (
    <Box sx={{ p: 3, height: "100%", overflow: "auto" }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 2 }} flexWrap="wrap">
        <Box>
          <Typography variant="h5">Serie mensual por collector</Typography>
          <Typography variant="body2" color="text.secondary">
            {source === "liquidado" ? (
              <>Monto <strong>liquidado</strong> (base de facturación) por mes y collector. Fuente:
              ar_operations — suma de <code>net_amount</code> de las liquidaciones (completed/credited,
              sin devoluciones) cuya <strong>fecha de liquidación</strong> cae en el mes.</>
            ) : (
              <>Cantidad y monto de <strong>cobros</strong> por mes y collector. Fuente: received_payments
              (lo que entró), agregado a diario. El mes se arma por <strong>fecha de negocio</strong>.</>
            )}
          </Typography>
        </Box>
      </Stack>

      <Stack direction="row" alignItems="center" spacing={2} flexWrap="wrap" sx={{ mb: 2 }} useFlexGap>
        <TextField
          size="small" select label="Base" value={source} sx={{ minWidth: 170 }}
          onChange={(e) => setSource(e.target.value as SerieSource)}
        >
          <MenuItem value="liquidado">Liquidado (facturación)</MenuItem>
          <MenuItem value="cobrado">Cobrado</MenuItem>
        </TextField>
        <TextField
          size="small" select label="Meses" value={rango} sx={{ minWidth: 190 }}
          onChange={(e) => setRango(e.target.value as Rango)}
        >
          {RANGOS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
        </TextField>
        <TextField
          size="small" label="Buscar collector" value={filtro}
          onChange={(e) => setFiltro(e.target.value)}
        />
        {hayCuentaCompartida && (
          <TextField
            size="small" select label="Cuenta CMF" value={cuentaActiva} sx={{ minWidth: 250 }}
            onChange={(e) => setCuenta(e.target.value as Cuenta)}
          >
            {CUENTAS.map((o) => <MenuItem key={o.value} value={o.value}>{o.label}</MenuItem>)}
          </TextField>
        )}
        <FormControlLabel
          control={<Switch checked={soloActivos} onChange={(e) => setSoloActivos(e.target.checked)} />}
          label="Solo con actividad"
        />
        <Tooltip title="Refrescar ahora">
          <span><IconButton onClick={load} disabled={loading}><RefreshIcon /></IconButton></span>
        </Tooltip>
        <Tooltip title="Serie por collector (lo que se ve en la tabla), meses en columnas">
          <span>
            <Button
              size="small" variant="outlined" startIcon={<DownloadIcon />}
              onClick={descargarCsv} disabled={!acq || rows.length === 0}
            >
              Serie (CSV)
            </Button>
          </span>
        </Tooltip>
        {source === "liquidado" && (
          <Tooltip title="Monto liquidado por día sumando TODOS los collectors del procesador y la cuenta elegidos">
            <span>
              <Button
                size="small" variant="contained" startIcon={<DownloadIcon />}
                onClick={descargarDiario} disabled={!acq || diarioBusy}
              >
                {diarioBusy ? "Generando…" : "Liquidado diario (CSV)"}
              </Button>
            </span>
          </Tooltip>
        )}
        {(loading || diarioBusy) && <CircularProgress size={18} />}
        {data && (
          // CP-424: "Última actualización" era la hora del browser al terminar el fetch,
          // no decía nada sobre hasta qué fecha de negocio llegan los datos — se leía
          // como si incluyera liquidaciones de recién. "Datos hasta" usa coverage.through_date
          // (ya viaja en la respuesta) y queda separado de cuándo se ejecutó la consulta.
          <Stack spacing={0}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Datos hasta: {fmtBd(data.coverage.through_date || data.coverage.last_date)}
            </Typography>
            {updatedAt && (
              <Typography variant="caption" color="text.secondary">
                Consulta ejecutada: {fmtArDateTime(updatedAt)}
              </Typography>
            )}
          </Stack>
        )}
      </Stack>

      {err && <Alert severity="error" sx={{ mb: 2 }}>{err}</Alert>}
      {data && data.warnings.length > 0 && (
        <Alert severity="warning" sx={{ mb: 2 }}>
          {data.warnings.map((w) => <div key={w}>{w}</div>)}
        </Alert>
      )}

      {data && acq && (
        <Stack spacing={2}>
          <Tabs value={activeProc} onChange={(_e, v) => setProc(v)} variant="scrollable" allowScrollButtonsMobile>
            {data.acquirers.map((a) => (
              <Tab key={a.key} value={a.key}
                   label={`${a.key} (${nf.format(a.collectors.length)})`} />
            ))}
          </Tabs>

          <Stack direction="row" spacing={2} flexWrap="wrap" useFlexGap>
            <StatTile
              label={`Operaciones (${meses.length} ${meses.length === 1 ? "mes" : "meses"})`}
              value={nf.format(totalMostrado.count)} />
            <StatTile label={source === "liquidado" ? "Monto liquidado" : "Monto cobrado"} value={fmtMoney(totalMostrado.amount, acq.currency)} />
            {variacion && (
              <StatTile
                label="Último mes cerrado"
                value={`${variacion.pct >= 0 ? "+" : ""}${variacion.pct.toFixed(1)} %`}
                sub={`${fmtMonth(variacion.last)} vs ${fmtMonth(variacion.prev)}`}
                color={variacion.pct >= 0 ? "#166534" : "#B91C1C"}
              />
            )}
          </Stack>

          <Alert severity="info" variant="outlined" icon={false} sx={{ py: 0.5 }}>
            <Typography variant="body2">
              <strong>Cobertura:</strong> {fmtBd(data.coverage.first_date)} → {fmtBd(data.coverage.last_date)}
              {"  ·  "}{nf.format(rows.length)} de {nf.format(collectorsUniverso)} collectors
              {cuentaActiva !== "todas" && ` (${cuentaActiva === "comp" ? `cuenta ${CUENTA_CMF_COMPARTIDA}` : "cuentas propias"})`}
              {"  ·  "}Clic en una celda para ver el detalle diario
            </Typography>
          </Alert>

          {/* CP-424: el mes en curso sale de un rollup que se recalcula una sola vez
              por día (madrugada) — no es un feed en vivo. Avisar siempre, en vez de
              dejar que una liquidación reciente quede invisible sin ninguna indicación. */}
          {data.coverage.partial_month && meses.includes(data.coverage.partial_month) && (
            <Alert severity="warning" variant="outlined" icon={false} sx={{ py: 0.5 }}>
              <Typography variant="body2">
                <strong>{fmtMonth(data.coverage.partial_month)} es el mes en curso:</strong> este
                total se recalcula una vez por día (madrugada) — {source === "liquidado" ? "una liquidación" : "un cobro"} de
                las últimas horas puede no estar reflejado todavía acá. El <strong>detalle diario</strong> de
                la celda (clic) sí consulta en vivo y puede no coincidir con este total hasta la próxima actualización.
              </Typography>
            </Alert>
          )}

          <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 640 }}>
            <Table size="small" stickyHeader>
              <TableHead>
                <TableRow>
                  <TableCell sx={{ position: "sticky", left: 0, zIndex: 3, bgcolor: "#fff", minWidth: 200 }}>
                    Collector
                  </TableCell>
                  {meses.map((m) => (
                    <TableCell key={m} align="right" sx={{ whiteSpace: "nowrap" }}>
                      {fmtMonth(m)}
                      {m === data.coverage.partial_month && (
                        <Tooltip title={`Mes en curso, agregado hasta el ${fmtBd(data.coverage.through_date)}`}>
                          <Chip size="small" label="parcial" sx={{ ml: 0.5, height: 18, fontSize: 10 }} />
                        </Tooltip>
                      )}
                    </TableCell>
                  ))}
                </TableRow>
              </TableHead>
              <TableBody>
                {rows.map((r) => {
                  const abierto = detalle?.acq === activeProc && detalle?.collector === r.collector;
                  return [
                    <TableRow key={r.collector} hover>
                      <TableCell sx={{ position: "sticky", left: 0, zIndex: 2, bgcolor: "#fff" }}>
                        <Stack direction="row" alignItems="center" spacing={0.5}>
                          {abierto ? <KeyboardArrowDownIcon fontSize="small" /> : <KeyboardArrowRightIcon fontSize="small" />}
                          <Box>
                            <Typography variant="body2" sx={{ fontWeight: 600 }}>{r.collector}</Typography>
                            {r.sub_processor && (
                              <Typography variant="caption" color="text.secondary">
                                {r.sub_processor}{r.account ? ` (${r.account})` : ""}
                              </Typography>
                            )}
                          </Box>
                        </Stack>
                      </TableCell>
                      {meses.map((m) => {
                        const openCell = abierto && detalle?.month === m;
                        // CP-424: si esta es la celda que el usuario ya abrió, es el mes en
                        // curso, y el detalle diario (en vivo) ya resolvió para este collector
                        // y mes — usar ESE total en vez del rollup guardado. No dispara ningún
                        // request nuevo: `dias` ya está pedido para pintar la tabla de abajo.
                        const live = openCell && m === data.coverage.partial_month
                          && !!dias && dias.collector === r.collector && dias.month === m
                          && !diasLoading && !diasErr;
                        const cell = live
                          ? { count: dias!.total.count, amount: dias!.total.amount,
                              avg: dias!.total.count ? dias!.total.amount / dias!.total.count : 0,
                              days: dias!.days.length }
                          : r.months[m];
                        return (
                          <MesCell
                            key={m} cell={cell} currency={acq.currency}
                            open={openCell} live={live}
                            onOpen={() => toggleCelda(r.collector, m)}
                          />
                        );
                      })}
                    </TableRow>,
                    abierto && detalle && (
                      <TableRow key={`${r.collector}-detalle`}>
                        <TableCell colSpan={meses.length + 1} sx={{ bgcolor: "#F8FAFC" }}>
                          <Typography variant="caption" sx={{ fontWeight: 700 }}>
                            Detalle diario — {r.collector} · {fmtMonth(detalle.month)}
                          </Typography>
                          {diasLoading && <CircularProgress size={16} sx={{ ml: 1 }} />}
                          {diasErr && <Alert severity="error" sx={{ mt: 1 }}>{diasErr}</Alert>}
                          {dias && !diasLoading && (
                            dias.days.length === 0 ? (
                              <Typography variant="body2" color="text.secondary">
                                {source === "liquidado" ? "Sin liquidaciones ese mes." : "Sin cobros ese mes."}
                              </Typography>
                            ) : (
                              <Table size="small" sx={{ mt: 1, maxWidth: 560 }}>
                                <TableHead>
                                  <TableRow>
                                    <TableCell>Día</TableCell>
                                    <TableCell align="right">Cantidad</TableCell>
                                    <TableCell align="right">Monto</TableCell>
                                    <TableCell align="right">Promedio</TableCell>
                                  </TableRow>
                                </TableHead>
                                <TableBody>
                                  {dias.days.map((d) => (
                                    <TableRow key={d.date}>
                                      <TableCell>{fmtBd(d.date)}</TableCell>
                                      <TableCell align="right">{nf.format(d.count)}</TableCell>
                                      <TableCell align="right">{fmtMoney(d.amount, dias.currency)}</TableCell>
                                      <TableCell align="right">{fmtMoney(d.avg, dias.currency)}</TableCell>
                                    </TableRow>
                                  ))}
                                  <TableRow sx={{ bgcolor: "#F1F5F9" }}>
                                    <TableCell sx={{ fontWeight: 700 }}>TOTAL</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{nf.format(dias.total.count)}</TableCell>
                                    <TableCell align="right" sx={{ fontWeight: 700 }}>{fmtMoney(dias.total.amount, dias.currency)}</TableCell>
                                    <TableCell />
                                  </TableRow>
                                </TableBody>
                              </Table>
                            )
                          )}
                        </TableCell>
                      </TableRow>
                    ),
                  ];
                })}
                <TableRow sx={{ bgcolor: "#F8FAFC" }}>
                  <TableCell sx={{ position: "sticky", left: 0, zIndex: 2, bgcolor: "#F8FAFC", fontWeight: 700 }}>
                    {etiquetaTotal}
                  </TableCell>
                  {meses.map((m) => {
                    const t = totalesPorMes[m];
                    return (
                      <TableCell key={m} align="right" sx={{ fontWeight: 700 }}>
                        <Typography variant="body2" sx={{ fontWeight: 700 }}>
                          {nf.format(t?.count ?? 0)}
                        </Typography>
                        <Typography variant="caption" color="text.secondary">
                          {fmtMoney(t?.amount ?? 0, acq.currency)}
                        </Typography>
                      </TableCell>
                    );
                  })}
                </TableRow>
                {rows.length === 0 && (
                  <TableRow><TableCell colSpan={meses.length + 1}>
                    <Typography variant="body2" color="text.secondary">Sin collectors para mostrar.</Typography>
                  </TableCell></TableRow>
                )}
              </TableBody>
            </Table>
          </TableContainer>
        </Stack>
      )}
    </Box>
  );
}
